import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth-guard';
import { orderStatusGuard } from './core/services/order/order-status.guard';

export const routes: Routes = [
  // --- Auth Routes ---
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  {
    path: 'login',
    loadComponent: () => import('./layout/auth/login/login').then(m => m.LoginComponent),
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./layout/auth/forgot-password/forgot-password').then(m => m.ForgotPassword),
  },
  {
    path: 'register',
    loadComponent: () => import('./layout/auth/registration/registration').then(m => m.Registration),
  },

  // --- Protected Main Application Area ---
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./layout/main/main-layout/main-layout').then(m => m.MainLayout),
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('./layout/pages/dashboard/dashboard').then(m => m.Dashboard),
      },

      // --- Inventory Management ---
      {
        path: 'inventory',
        children: [
          { path: 'category', loadComponent: () => import('./layout/pages/inventory-management/category/category').then(m => m.Category) },
          { path: 'category/:id', loadComponent: () => import('./layout/pages/inventory-management/category/product-list/product-list').then(m => m.ProductList) },
          { path: 'products', loadComponent: () => import('./layout/pages/inventory-management/products/products').then(m => m.Products) },
          { path: 'products/:id', loadComponent: () => import('./layout/pages/inventory-management/products/product-detail/product-detail').then(m => m.ProductDetail) },
          { path: 'stocks', loadComponent: () => import('./layout/pages/inventory-management/stocks/stocks').then(m => m.Stocks) },
          { path: 'add-stock', loadComponent: () => import('./layout/pages/inventory-management/stocks/stock-update/stock-update').then(m => m.StockUpdate) },
          { path: 'remove-stock', loadComponent: () => import('./layout/pages/inventory-management/stocks/stock-update/stock-update').then(m => m.StockUpdate) },
        ]
      },

      // --- Distributors & Customers ---
      {
        path: 'distributors',
        children: [
          { path: '', loadComponent: () => import('./layout/pages/distributors/distributors').then(m => m.Distributors) },
          { path: 'add', loadComponent: () => import('./layout/pages/distributors/add-distributor/add-distributor').then(m => m.AddDistributor) },
          { path: 'add/:id', loadComponent: () => import('./layout/pages/distributors/add-distributor/add-distributor').then(m => m.AddDistributor) },
          { path: ':id', loadComponent: () => import('./layout/pages/distributors/distributor-profile/distributor-profile').then(m => m.DistributorProfile) },
        ]
      },
      {
        path: 'customers',
        children: [
          { path: '', loadComponent: () => import('./layout/pages/customers/customers').then(m => m.Customers) },
          { path: ':id', loadComponent: () => import('./layout/pages/customers/customer-details/customer-details').then(m => m.CustomerDetails) },
        ]
      },

      // --- Order Management & Process Flow ---
      {
        path: 'orders',
        children: [
          { path: '', loadComponent: () => import('./layout/pages/orders/orders').then(m => m.Orders) },
          { path: 'aod/:id', loadComponent: () => import('./layout/pages/orders/order-process/order-aod').then(m => m.OrderAodComponent) },
          { path: 'authorize', loadComponent: () => import('./layout/pages/orders/authorize/authorize').then(m => m.Authorize) },
          { path: 'create/:mode', loadComponent: () => import('./layout/pages/orders/create-order/create-order').then(m => m.CreateOrder) },
          { path: 'create/customer/confirm/:id', loadComponent: () => import('./layout/pages/orders/invoice/invoice').then(m => m.Invoice) },
          { path: 'create/customer/invoice/:id', loadComponent: () => import('./layout/pages/orders/invoice-view/invoice-view').then(m => m.InvoiceView) },

          // Nested Process Flow with Guard
          {
            path: 'process/:id',
            loadComponent: () => import('./layout/pages/orders/order-process/order-process').then(m => m.OrderProcess),
            children: [
              { path: '', redirectTo: 'authorize', pathMatch: 'full' },
              { path: 'authorize',   canActivate: [orderStatusGuard], loadComponent: () => import('./layout/pages/orders/order-process/authorize/authorize').then(m => m.StepAuthorize) },
              { path: 'preparation', canActivate: [orderStatusGuard], loadComponent: () => import('./layout/pages/orders/order-process/preparation/preparation').then(m => m.Preparation) },
              { path: 'dispatch',    canActivate: [orderStatusGuard], loadComponent: () => import('./layout/pages/orders/order-process/dispatch/dispatch').then(m => m.Dispatch) },
              { path: 'delivery',    canActivate: [orderStatusGuard], loadComponent: () => import('./layout/pages/orders/order-process/delivery/delivery').then(m => m.Delivery) },
              { path: 'payments',    canActivate: [orderStatusGuard], loadComponent: () => import('./layout/pages/orders/order-process/payments/payments').then(m => m.Payments) },
              { path: 'hold',        canActivate: [orderStatusGuard], loadComponent: () => import('./layout/pages/orders/order-process/hold/hold').then(m => m.Hold) },
              { path: 'cancel',      canActivate: [orderStatusGuard], loadComponent: () => import('./layout/pages/orders/order-process/cancel/cancel').then(m => m.Cancel) },
              { path: 'reject',      canActivate: [orderStatusGuard], loadComponent: () => import('./layout/pages/orders/order-process/reject/reject').then(m => m.Reject) },
            ]
          }
        ]
      },

      // --- Invoices & Finance ---
      { path: 'invoice', loadComponent: () => import('./layout/pages/invoice/invoice').then(m => m.Invoice) },
      { path: 'invoice/:id', loadComponent: () => import('./layout/pages/invoice/invoice-details/invoice-details').then(m => m.InvoiceDetails) },
      { path: 'finance', loadComponent: () => import('./layout/pages/accounts/accounts').then(m => m.Accounts) },
      { path: 'finance/invoice/:id', loadComponent: () => import('./layout/pages/accounts/invoice-view/invoice-view').then(m => m.InvoiceView) },
      { path: 'finance/payments', loadComponent: () => import('./layout/pages/accounts/payments-summary/payments-summary').then(m => m.PaymentsSummary) },
      {
        path: 'finance/distributor-refund',
        loadComponent: () => import('./layout/pages/accounts/distributor-refund/distributor-refund').then(m => m.DistributorRefund)
      },
      // --- Returns & Warranty ---
      {
        path: 'return',
        children: [
          { path: '', loadComponent: () => import('./layout/pages/return/return').then(m => m.Return) },
          { path: 'add', loadComponent: () => import('./layout/pages/return/add-return/add-return').then(m => m.AddReturn) },
          { path: 'confirm', loadComponent: () => import('./layout/pages/return/return-confirm/return-confirm').then(m => m.ReturnConfirm) },
          { path: ':id', loadComponent: () => import('./layout/pages/return/return-invoice/return-invoice').then(m => m.ReturnInvoice) },
        ]
      },
      {
        path: 'warranty-claim',
        children: [
          { path: '', loadComponent: () => import('./layout/pages/warranty-claim/warranty-claim').then(m => m.WarrantyClaim) },
          { path: 'add-distributor', loadComponent: () => import('./layout/pages/warranty-claim/add-warranty/add-warranty').then(m => m.AddWarranty) },
          { path: 'add-customer', loadComponent: () => import('./layout/pages/warranty-claim/add-customer-warranty/add-customer-warranty').then(m => m.AddCustomerWarranty) },
          { path: 'confirm', loadComponent: () => import('./layout/pages/warranty-claim/confirm-warranty/confirm-warranty').then(m => m.ConfirmWarranty) },
        ]
      },
       {
        path: 'reports',
        loadComponent: () => import('./layout/pages/reports/reports').then(m => m.Reports),
      },

      // --- Settings (Roles & Users) ---
      {
        path: 'settings',
        children: [
          { path: 'add-role', loadComponent: () => import('./layout/pages/settings/roles/add-role-page/add-role-page').then(m => m.AddRolePage) },
          { path: 'view-role/:id', loadComponent: () => import('./layout/pages/settings/roles/add-role-page/add-role-page').then(m => m.AddRolePage) },
          { path: 'edit-role/:id', loadComponent: () => import('./layout/pages/settings/roles/add-role-page/add-role-page').then(m => m.AddRolePage) },
          { path: 'add-user', loadComponent: () => import('./layout/pages/settings/users/add-user/add-user').then(m => m.AddUser) },
          { path: 'edit-user/:id', loadComponent: () => import('./layout/pages/settings/users/user-edit-view-page/user-edit-view-page').then(m => m.UserEditViewPage) },
          { path: 'view-user/:id', loadComponent: () => import('./layout/pages/settings/users/user-edit-view-page/user-edit-view-page').then(m => m.UserEditViewPage) },
          { path: ':tab', loadComponent: () => import('./layout/pages/settings/settings').then(m => m.Settings) },
        ]
      }
    ]
  }
];

